/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Camera,
  Music,
  Utensils,
  Car,
  Scissors,
  ArrowRight,
  Sparkles,
  Star,
  ChevronLeft,
  ChevronRight,
  Users,
} from "lucide-react";
import { useServiceCategories } from "../../services/serviceCategoryService";

// Add CSS for hiding scrollbars
const scrollbarHideCSS = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

// Inject CSS
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = scrollbarHideCSS;
  document.head.appendChild(style);
}

const ServiceCategories = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [counter, setCounter] = useState(5000);
  const scrollContainerRef = React.useRef(null);
  const autoScrollRef = React.useRef(null);
  const counterRef = React.useRef(null);

  // Fetch service categories from API
  const { data: categoriesData, isLoading } = useServiceCategories({
    active: true,
    sort_by: "sort_order",
    sort_order: "asc",
    per_page: 20,
  });

  const categories = categoriesData?.data?.data || [];
  // console.log(categories);
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Counter animation effect
  useEffect(() => {
    if (isVisible) {
      counterRef.current = setInterval(() => {
        setCounter((prev) => {
          // Increment by 1-3 randomly for natural counting effect
          const increment = Math.floor(Math.random() * 3) + 1;
          return prev + increment;
        });
      }, 100); // Update every 100ms
    }

    return () => {
      if (counterRef.current) {
        clearInterval(counterRef.current);
      }
    };
  }, [isVisible]);

  // Get emoji for category - use from database or fallback to mapping
  const getEmojiForCategory = (category) => {
    // Use emoji from database if available
    if (category.emoji) {
      return category.emoji;
    }

    // Fallback to category name mapping
    const name = category.category_name.toLowerCase();
    if (name.includes("photo") || name.includes("video")) return "📸";
    if (name.includes("entertainment") || name.includes("music")) return "🎵";
    if (
      name.includes("event") ||
      name.includes("catering") ||
      name.includes("food")
    )
      return "🍽️";
    if (name.includes("transport") || name.includes("car")) return "🚗";
    if (
      name.includes("beauty") ||
      name.includes("wellness") ||
      name.includes("hair")
    )
      return "💄";
    if (name.includes("decoration") || name.includes("decor")) return "🎨";
    if (name.includes("venue") || name.includes("location")) return "🏢";
    return "📣"; // default icon
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setScrollPosition(scrollLeft);
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial check
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [categories]);

  // Auto-scroll functionality
  useEffect(() => {
    if (categories.length > 0) {
      autoScrollRef.current = setInterval(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const scrollAmount = 1; // Slow scroll speed

          container.scrollLeft += scrollAmount;

          // Calculate the width of one complete set of cards
          const cardWidth = 176; // w-44 = 176px
          const gap = 12; // space-x-3 = 12px
          const singleSetWidth = categories.length * (cardWidth + gap);

          // Reset to beginning when we've scrolled past the first set
          if (container.scrollLeft >= singleSetWidth) {
            container.scrollLeft = 0;
          }
        }
      }, 50); // Scroll every 50ms for smooth movement
    }

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [categories]);

  // Pause auto-scroll on hover
  const handleMouseEnter = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (categories.length > 0) {
      autoScrollRef.current = setInterval(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const scrollAmount = 1;

          container.scrollLeft += scrollAmount;

          // Calculate the width of one complete set of cards
          const cardWidth = 176; // w-44 = 176px
          const gap = 12; // space-x-3 = 12px
          const singleSetWidth = categories.length * (cardWidth + gap);

          // Reset to beginning when we've scrolled past the first set
          if (container.scrollLeft >= singleSetWidth) {
            container.scrollLeft = 0;
          }
        }
      }, 50);
    }
  };

  if (isLoading) {
    return (
      <section className="py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="page-container">
          <div className="text-center mb-12">
           
            <h2 className="text-3xl md:text-3xl font-semibold text-gray-900 mb-4">
              Everything You Need for Your Event
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Loading categories...
            </p>
          </div>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    
    // background: background: var(--search-input_background,linear-gradient(180deg,#fff 39.9%,#f8f8f8 100%));
  // border-bottom: 1px solid #e4e4e4;
    <section className="pt-20 relative overflow-hidden" style={{borderBottom: "1px solid #e4e4e4", background: "var(--search-input_background,linear-gradient(180deg,#fff 39.9%,#f8f8f8 100%))"}}>
      <div className="page-container">
        {/* Header */}
        <div className="text-center mb-16">

          <div
            className={`transform transition-all duration-1000 delay-200 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            }`}
          >
            <h2 className="text-2xl md:text-2xl font-semibold text-gray-900 mb-4">
              Everything You Need for Your Event
            </h2>
          </div>

          <div
            className={`transform transition-all duration-1000 delay-400 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            }`}
          >
            <p className="text-md text-gray-600 max-w-2xl mx-auto">
              Professional services to make your event unforgettable
            </p>
          </div>
        </div>

        {/* Horizontal Scrolling Categories */}
        <div className="relative mb-10">
          {/* Scroll Buttons */}
          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm hover:bg-white border border-gray-200 rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}

          {canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm hover:bg-white border border-gray-200 rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          )}

          {/* Fade gradients on both ends */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex space-x-3 overflow-x-auto scrollbar-hide pb-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* First set of cards */}
            {categories.map((category, index) => {
              const emojiIcon = getEmojiForCategory(category);

              return (
                <Link
                  key={`first-${category.id}`}
                  to={`/category/${category.id}?title=${encodeURIComponent(category.category_name || 'Services')}`}
                  className="group flex-shrink-0"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div
                    className={`transform transition-all duration-700 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-20 opacity-0"
                    }`}
                  >
                    {/* Simple Card Container */}
                    <div className="w-44 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 group-hover:scale-105 border border-gray-100 p-6 text-center">
                      {/* Emoji Icon */}
                      <div className="flex justify-center mb-2">
                        <span className="text-3xl">{emojiIcon}</span>
                      </div>

                      {/* Text - Single Line */}
                      <h3 className="text-sm font-normal text-gray-700 group-hover:text-black transition-colors duration-300 whitespace-nowrap overflow-hidden text-ellipsis">
                        {category.category_name}
                      </h3>
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* Duplicate set for infinite scroll */}
            {categories.map((category, index) => {
              const emojiIcon = getEmojiForCategory(category);

              return (
                <Link
                  key={`second-${category.id}`}
                  to={`/category/${category.id}?title=${encodeURIComponent(category.category_name || 'Services')}`}
                  className="group flex-shrink-0"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div
                    className={`transform transition-all duration-700 ${
                      isVisible
                        ? "translate-y-0 opacity-100"
                        : "translate-y-20 opacity-0"
                    }`}
                  >
                    {/* Simple Card Container */}
                    <div className="w-44 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 group-hover:scale-105 border border-gray-100 p-6 text-center">
                      {/* Emoji Icon */}
                      <div className="flex justify-center mb-2">
                        <span className="text-3xl">{emojiIcon}</span>
                      </div>

                      {/* Text - Single Line */}
                      <h3 className="text-sm font-normal text-gray-700 group-hover:text-black transition-colors duration-300 whitespace-nowrap overflow-hidden text-ellipsis">
                        {category.category_name}
                      </h3>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Statistics Counter */}
        {/* <div
          className={`text-center transform transition-all duration-1000 delay-600 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Users className="w-4 h-4 text-gray-600" />

            <span className="text-gray-700 text-sm">
              <span className="bg-gold-100 text-gold-800 px-2 py-1 rounded-full font-light text-sm">
                {counter.toLocaleString()}
              </span>
              <span className="ml-2 text-gray-600">
                events successfully booked
              </span>
            </span>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default ServiceCategories;
